import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Icon } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetSites, dataSetInspectionPoints, dataSetContactInfos, dataSetFtpDetails } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import SiteHttpService from '../../../services/http/masterData/siteHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import InspectionPointHttpService from '../../../services/http/masterData/inspectionPointHttpService';
import Screen from '../../../components/Screen';
import SiteForm from './form/SiteForm';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import FloatingActionButton from '../../../components/input/FloatingActionButton';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import ContactInfoHttpService from '../../../services/http/masterData/contactInfoHttpService';
import CustomTooltip from '../../../components/tooltip/tooltip';
import { setSiteMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import FtpDetailHttpService from '../../../services/http/masterData/ftpDetailHttpService';
import { IApiError } from '../../../@types/other';
import { ISite } from '../../../@types/model/masterData/site/site';
import { ISiteFormValues } from '../../../@types/model/masterData/site/siteFormValues';
import SiteModelHelper from '../../../@types/model/masterData/site/siteModelHelper';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';

const SiteScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const sites = useAppSelector(x => x.masterData.sites);
    const inspectionPoints = useAppSelector(x => x.masterData.inspectionPoints);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);
    const organizations = useAppSelector(x => x.masterData.organizations);
    const contactInfos = useAppSelector(x => x.masterData.contactInfos);
    const ftpDetails = useAppSelector(x => x.masterData.ftpDetails);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedSite, setSelectedSite] = useState<ISite | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingSite, setDeletingSite] = useState<ISite | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await SiteHttpService.getSiteData();
                const res2 = await ContactInfoHttpService.getContactInfoData();
                const res3 = await FtpDetailHttpService.getFtpDetailData();

                dispatch(dataSetSites(res.data));
                dispatch(dataSetContactInfos(res2.data));
                dispatch(dataSetFtpDetails(res3.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | null;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading sites.');
            } finally {
                setIsLoading(false);
            }

            try {
                const res = await InspectionPointHttpService.getInspectionPointData();
                dispatch(dataSetInspectionPoints(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | null;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading inspection points.');
            }
        }
    };

    const onSubmit = async (values : ISiteFormValues) => {
        setIsLoading(true);

        try {
            const upsert = SiteModelHelper.createUpsert(values);
            const res = await SiteHttpService.addOrUpdateSite(upsert);
            const updatedList = upsertArrayElement(sites, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetSites(updatedList));
            await setSiteMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedSite ? 'Site updated successfully.' : 'Site added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | null;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating site data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeSite = async () => {
        if (!deletingSite) return;
        const updatedSite = { ...deletingSite, isActive: false };
        setIsLoading(true);
        try {
            const res = await SiteHttpService.deleteSite(updatedSite.id);
            const updatedList = upsertArrayElement(sites, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetSites(updatedList));
            await setSiteMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Site deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | null;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting site.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedSite(null);
    };

    const edit = (selectedRow : ISite) => {
        setSelectedSite(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ISite) : void => {
        setDeletingSite(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingSite(null);
    };

    const deleteButtonTooltip = (row : ISite) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Site', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const getInspectionPointCode = useCallback((id : number) => {
        const item = inspectionPoints.find(x => x.id === id);
        return item ? item.code : '';
    }, [inspectionPoints]);

    const formatOrganizations = useCallback((organizationIds : Array<number>) => {
        return organizations
            .filter(x => organizationIds.some(y => y === x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
    }, [organizations]);

    const formatContactInfos = useCallback((contactInfoIds : Array<number>) => {
        return contactInfos
            .filter(x => contactInfoIds.some(y => y === x.id))
            .map(x => x.name).toString().replace(/,/g, ', ');
    }, [contactInfos]);

    const formatFtpDetails = useCallback((ftpDetailIds : Array<number>) => {
        return ftpDetails
            .filter(x => ftpDetailIds.some(y => y === x.id))
            .map(x => x.name).toString().replace(/,/g, ', ');
    }, [ftpDetails]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return SiteModelHelper.createFormValues(
                selectedSite,
                organizations,
                contactInfos,
                ftpDetails,
                inspectionPoints
            );
        }
    }, [
        selectedSite,
        organizations,
        contactInfos,
        ftpDetails,
        inspectionPoints,
        isDialogOpen
    ]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo(() => {
        return sites.filter(x => x.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y)));
    }, [sites, selectedOrganizationIds]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div style={{ right: 310, bottom: 25 }} className={`${isLoading ? 'dn' : 'fdr posa zi999'}`}>
                <CustomTooltip title={'Map of Sites'}>
                    <Link to={'/overview/sitesMap'} style={{ textDecoration: 'unset', color: 'unset' }}>
                        <FloatingActionButton
                            color={'secondary'}
                            size={'medium'}
                            className={'mr5 p0 aic jcc'}
                        >
                            <Icon className={'cpd'} fontSize={'medium'} >room</Icon>
                        </FloatingActionButton>
                    </Link>
                </CustomTooltip>
            </div>
            <div className={'fdc hfill'}>
                <Card className={'fdc hfill'}>
                    <CustomTable<ISite>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Site', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Site', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : ISite) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Short Description', field: 'shortDescription', enableFiltering: true, enableSorting: true },
                            { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                            { title: 'Organizations', field: 'organizationIds', formatFunction: formatOrganizations, width: 250, enableFiltering: true, enableSorting: true },
                            { title: 'Contact Infos', field: 'contactInfoIds', formatFunction: formatContactInfos, width: 250, enableFiltering: true, enableSorting: true },
                            { title: 'FTP Details', field: 'ftpDetailIds', formatFunction: formatFtpDetails, width: 250, enableFiltering: true, enableSorting: true },
                            { title: 'Inspection Point', field: 'inspectionPointId', formatFunction: getInspectionPointCode, enableFiltering: true, enableSorting: true },
                            { title: 'Exporter Name', field: 'exporterName', enableFiltering: true, enableSorting: true },
                            { title: 'Exporter Email', field: 'exporterEmail', enableFiltering: true, enableSorting: true },
                            { title: 'Global Gap Number', field: 'globalGapNumber', enableFiltering: true, enableSorting: true },
                            { title: 'Packhouse Date Code', field: 'packhouseDateCode', enableFiltering: true, enableSorting: true },
                            { title: 'Tel Number', field: 'telNumber', enableFiltering: true, enableSorting: true },
                            { title: 'Fax Number', field: 'faxNumber', enableFiltering: true, enableSorting: true },
                            { title: 'Packhouse?', field: 'isPackhouse', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Delivery Point?', field: 'isDeliveryPoint', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'External?', field: 'isExternal', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Handles Export?', field: 'handlesExport', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'BRC Compliant?', field: 'isBRCCompliant', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Simple?', field: 'isSimple', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Can Mix Sales Point?', field: 'canMixSalesPoint', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={rows}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedSite ? 'Edit Site' : 'Add Site'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'xl'}
                    fullWidth
                >
                    <SiteForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                        organizations={organizations}
                        contactInfos={contactInfos}
                        ftpDetails={ftpDetails}
                        inspectionPoints={inspectionPoints}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Site'}
                open={!!deletingSite}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeSite}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default SiteScreen;
